import { Select, Space, Typography } from 'antd'
import { LabeledValue } from 'antd/es/select'
import { DrilldownTypeAndFieldSelect } from 'features/drilldown/components/fieldSelect/TypeAndFieldSelect'
import { HistogramConfigurationOption } from 'features/drilldown/components/histogram/configPanel/histogramConfigTypes'
import { useHistogramConfiguration } from 'features/drilldown/components/histogram/contexts/HistogramConfigurationContext'
import {
  HISTOGRAM_DATE_BUCKETING_OPTIONS,
  HistogramDateBucketingOption,
} from 'features/drilldown/components/histogram/histogramDateConfigurationOptions'
import { OntologyField, useOntology } from 'features/dynamicOntology'
import { FC, useCallback, useMemo } from 'react'

import { HistoAggregationOpSelect } from './HistoAggregationOpSelect'

const { Text } = Typography

export interface HistogramGroupByConfigurationOption extends HistogramConfigurationOption {
  // Generally for rolodex histogram groupByQueries:
  // We use 'field' to filter on top level properties like 'id' and
  // 'key' to filter on properties under 'data' like 'date'
  histogramGroupByPropertyKey: 'key' | 'field'

  // Do we truncate the results of the histogram.
  // For certain histogram types such as dates, truncation doesn't make sense
  shouldTruncateResults: boolean

  // Whether a user can switch a histogram to group on this value.
  isSelectableInUi?: boolean
}

interface HistoAggregationConfigViewProps {
  direction?: 'horizontal' | 'vertical'
}

export const HistoAggregationConfigView: FC<
  HistoAggregationConfigViewProps
> = ({ direction = 'horizontal' }) => {
  const {
    groupingColumn,
    setGroupingColumn,
    aggregationColumn,
    setAggregationColumn,
    dateBucketingConfiguration,
    setDateBucketingConfiguration,
    setAggregationOp,
  } = useHistogramConfiguration()
  const { fields, isDescendant } = useOntology()
  const groupingColumnDef = fields[groupingColumn]

  const isAggregationOptionValid = useCallback(
    (aggCol: OntologyField) => {
      if (!aggCol) {
        return false
      }
      const { entryType } = fields[aggCol]
      return (
        groupingColumnDef.entryType === entryType ||
        isDescendant(entryType, groupingColumnDef.entryType)
      )
    },
    [fields, groupingColumnDef.entryType, isDescendant]
  )

  // Only allow aggregations of rolodex types at levels downstream of the one currently selected
  const allowedAggregationFields = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(fields).filter(([field]) =>
          isAggregationOptionValid(field)
        )
      ),
    [fields, isAggregationOptionValid]
  )

  const onDateGroupingOptionChange = useCallback(
    (value: LabeledValue) => {
      setDateBucketingConfiguration?.(
        HISTOGRAM_DATE_BUCKETING_OPTIONS[
          value.value as HistogramDateBucketingOption
        ]
      )
    },
    [setDateBucketingConfiguration]
  )

  const dateBucketingConfigurationOptions = useMemo(
    () => Object.values(HISTOGRAM_DATE_BUCKETING_OPTIONS),
    []
  )

  const areColumnsConfigurable =
    !!setGroupingColumn ||
    !!setAggregationColumn ||
    !!setDateBucketingConfiguration

  if (!areColumnsConfigurable) {
    return null
  }

  return (
    <Space
      orientation={direction === 'vertical' ? 'vertical' : 'horizontal'}
      size='small'
      wrap={direction === 'horizontal'}
      style={{ width: '100%' }}>
      {!!setGroupingColumn && (
        <Space align='center' size='small'>
          <Text strong>Group by:</Text>
          <DrilldownTypeAndFieldSelect
            selectedField={groupingColumn}
            setSelectedField={setGroupingColumn}
          />
        </Space>
      )}

      {!!setAggregationColumn && (
        <Space align='center' size='small'>
          <Text strong>Aggregate:</Text>
          <DrilldownTypeAndFieldSelect
            selectedField={aggregationColumn}
            setSelectedField={setAggregationColumn}
            fields={allowedAggregationFields}
          />
        </Space>
      )}

      {!!setAggregationOp && (
        <Space align='center' size='small'>
          <Text strong>Method:</Text>
          <HistoAggregationOpSelect />
        </Space>
      )}

      {!!setDateBucketingConfiguration && (
        <Space align='center' size='small'>
          <Text strong>Time period:</Text>
          <Select
            style={{ minWidth: 130 }}
            labelInValue={true}
            options={dateBucketingConfigurationOptions}
            value={dateBucketingConfiguration}
            onChange={onDateGroupingOptionChange}
            placeholder='Select period'
          />
        </Space>
      )}
    </Space>
  )
}
